import {
  type CartItem,
  type CategoryDTO,
  type CheckoutProduct,
  type Option,
  type ProductLightDTO
} from 'ecosystem'
import { flatCartItems, getProductPrice } from 'shared-utils'
import { type GA4Item } from './ga4-events'

/**
 * Factory function to create GA4-compliant item objects
 * @param items - Cart items to convert
 * @param categoryMap - Optional map of category IDs to category names
 * @returns Array of GA4-formatted items
 */
export const ga4EcommerceItemFactoryFromCartItems = (
  items: CartItem[],
  categoryMap?: Record<string, string>
): GA4Item[] => {
  const flatItems: CheckoutProduct[] = flatCartItems(items).map(
    (cartItem) =>
      ({
        productId: cartItem.product.id,
        articleNumber: cartItem.product.articleNumber,
        name: cartItem.product.name,
        brand: cartItem.product.brand?.name,
        unitPrice: getProductPrice(cartItem.product),
        quantity: cartItem.qty,
        categoryIds: cartItem.product.categoryIds ?? undefined
      }) satisfies CheckoutProduct
  )

  return ga4EcommerceItemsFactory(flatItems, categoryMap)
}

/**
 * Factory function to create GA4-compliant item objects from products
 * @param products - Products
 * @param categoryMap - Optional map of category IDs to category names
 * @returns Array of GA4-formatted items
 */
export const ga4EcommerceItemFactoryFromProducts = (
  products: ProductLightDTO[],
  categoryMap?: Record<string, string>
): GA4Item[] => {
  const flatItems: CheckoutProduct[] = products.map(
    (product) =>
      ({
        productId: product.id,
        articleNumber: product.articleNumber,
        name: product.name,
        brand: product.brand?.name,
        unitPrice: getProductPrice(product),
        quantity: 1,
        categoryIds: product.categoryIds ?? undefined
      }) satisfies CheckoutProduct
  )

  return ga4EcommerceItemsFactory(flatItems, categoryMap)
}

export const ga4EcommerceItemsFactory = (
  items: CheckoutProduct[],
  categoryMap?: Record<string, string>
): GA4Item[] => {
  return items.map((item, index) => {
    const categoryInfo: Partial<
      Pick<
        GA4Item,
        'item_category' | 'item_category2' | 'item_category3' | 'item_category4' | 'item_category5'
      >
    > = {}

    if (item.categoryIds?.length) {
      const categoryKeys: (keyof Pick<
        GA4Item,
        'item_category' | 'item_category2' | 'item_category3' | 'item_category4' | 'item_category5'
      >)[] = [
        'item_category',
        'item_category2',
        'item_category3',
        'item_category4',
        'item_category5'
      ]

      for (let i = 0; i < Math.min(item.categoryIds.length, 5); i++) {
        const categoryId = item.categoryIds[i]
        const categoryKey = categoryKeys[i]
        const categoryName = categoryMap?.[categoryId]

        if (categoryName || categoryId) {
          categoryInfo[categoryKey] = categoryName || categoryId
        }
      }
    }

    return {
      item_id: item.articleNumber,
      item_name: item.name,
      item_brand: item.brand,
      price: item.unitPrice,
      quantity: item.quantity,
      index,
      ...categoryInfo
    } satisfies GA4Item
  })
}

export const createCategoryMap = (categories?: Option<CategoryDTO[]>) => {
  if (!categories) return {}

  const map: Record<string, string> = {}
  const processCategoryTree = (category: CategoryDTO) => {
    map[category.id] = category.name

    if (category.subCategories) {
      category.subCategories.forEach(processCategoryTree)
    }
  }

  categories.forEach(processCategoryTree)
  return map
}
